/**
 * Core entity shapes used by proposals ("create a calf", "add Cedar pasture") and generated forms.
 * Modules add attributes through ModuleContract.attributes; those land in attrs JSON.
 */
import { f, type Fields } from "./fields";

export type EntityKind = "animal" | "group" | "place" | "contact" | "stock_item" | "stock_lot" | "asset" | "fin_doc";

export const ANIMAL_SEX = { female: "Female", male: "Male", unknown: "Unknown sex" } as const;
export const ANIMAL_STATUS = {
  active: "In the herd", sold: "Sold", dead: "Dead", culled: "Culled", transferred: "Transferred", missing: "Missing", reference: "Reference only",
} as const;
export const ANIMAL_CLASS = {
  cow: "Cow", heifer: "Heifer", bull: "Bull", steer: "Steer", calf: "Calf", bull_calf: "Bull calf", heifer_calf: "Heifer calf",
  yearling: "Yearling", stocker: "Stocker", feeder: "Feeder", other: "Other",
} as const;
export const PLACE_KIND = {
  pasture: "Pasture", pen: "Pen", trap: "Trap", barn: "Barn", facility: "Facility", field: "Field", water: "Water", other: "Other",
} as const;
export const GROUP_KIND = {
  herd: "Herd", management: "Management group", marketing: "Marketing group", contemporary: "Contemporary group", lot: "Lot", saved_view: "Saved list",
} as const;
export const STOCK_KIND = {
  medicine: "Medicine", vaccine: "Vaccine", feed: "Feed", ingredient: "Feed ingredient", hay: "Hay", mineral: "Mineral", semen: "Semen",
  embryo: "Embryos", supply: "Supply", part: "Part", fuel: "Fuel", seed: "Seed", chemical: "Chemical", other: "Other",
} as const;
export const CONTACT_ROLES = {
  buyer: "Buyer", seller: "Seller", vet: "Veterinarian", lab: "Lab", supplier: "Supplier", owner: "Owner", association: "Association",
  trucker: "Trucker", auction: "Auction market", crew: "Crew", landlord: "Landlord", other: "Other",
} as const;
export const ASSET_KIND = {
  vehicle: "Vehicle", equipment: "Equipment", implement: "Implement", trailer: "Trailer", facility: "Facility", water: "Water system", fence: "Fence", other: "Other",
} as const;
export const FIN_KIND = {
  expense: "Expense", income: "Income", invoice: "Invoice", bill: "Bill", payment: "Payment", statement: "Statement", adjustment: "Adjustment",
} as const;

export const ENTITY_FIELDS: Record<EntityKind, Fields> = {
  animal: {
    tag: f.tag("Tag", { level: "core" }),
    name: f.text("Name", { level: "core" }),
    sex: f.enum("Sex", ANIMAL_SEX, { level: "core" }),
    castrated: f.bool("Castrated", { level: "more" }),
    birth_date: f.date("Birth date", { level: "core", agent: "Only when stated or implied by a birth event" }),
    birth_precision: f.enum("Birth date is", { date: "Exact", estimated_date: "Estimated", year: "Year only", unknown: "Unknown" }, { level: "more" }),
    breed: f.text("Breed", { level: "more" }),
    color: f.text("Color", { level: "more" }),
    dam_id: f.ref("Dam", "animal", { level: "more" }),
    sire_id: f.ref("Sire", "animal", { level: "more" }),
    class_override: f.enum("Class", ANIMAL_CLASS, { level: "more", help: "Hondo works this out from sex, age and calving. Set it only to override." }),
    status: f.enum("Status", ANIMAL_STATUS, { level: "more" }),
    origin: f.enum("Origin", { born: "Born here", purchased: "Purchased", received: "Received", imported: "Imported", unknown: "Unknown" }, { level: "more" }),
    eid: f.text("EID", { level: "more", help: "Electronic ID (RFID) number" }),
    registration: f.text("Registration number", { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  group: {
    name: f.text("Name", { required: true }),
    kind: f.enum("Kind", GROUP_KIND),
    place_id: f.ref("Usual place", "place", { level: "more" }),
    head_count: f.int("Counted head", { level: "more", min: 0, help: "For lots that are counted rather than individually tagged" }),
  },
  place: {
    name: f.text("Name", { required: true }),
    kind: f.enum("Kind", PLACE_KIND),
    area: f.quantity("Area", "area", { level: "more" }),
    capacity: f.int("Capacity (head)", { level: "more", min: 0 }),
    parent_id: f.ref("Part of", "place", { level: "more" }),
  },
  contact: {
    name: f.text("Name", { required: true }),
    kind: f.enum("Kind", { person: "Person", organization: "Business or organization" }),
    roles: f.multiEnum("Roles", CONTACT_ROLES),
    phone: f.text("Phone", { level: "more" }),
    email: f.text("Email", { level: "more" }),
    address: f.longText("Address", { level: "more" }),
    notes: f.longText("Notes", { level: "more" }),
  },
  stock_item: {
    name: f.text("Name", { required: true }),
    kind: f.enum("Kind", STOCK_KIND, { required: true }),
    unit: f.text("Counted in", { help: "mL, doses, bales, lb, straws..." }),
    reorder_at: f.number("Reorder at", { level: "more", min: 0 }),
  },
  stock_lot: {
    item_id: f.ref("Product", "stock_item", { required: true }),
    code: f.text("Lot or batch"),
    expires_at: f.date("Expires"),
    place_id: f.ref("Stored at", "place", { level: "more" }),
    quantity: f.number("Quantity on hand", { level: "core", min: 0, help: "Recorded as received" }),
  },
  asset: {
    name: f.text("Name", { required: true }),
    kind: f.enum("Kind", ASSET_KIND),
    make: f.text("Make", { level: "more" }),
    model: f.text("Model", { level: "more" }),
    serial: f.text("Serial or VIN", { level: "more" }),
    meter_unit: f.enum("Meter", { hours: "Hours", miles: "Miles", km: "Kilometers" }, { level: "more" }),
  },
  fin_doc: {
    kind: f.enum("Kind", FIN_KIND, { required: true }),
    contact_id: f.ref("With", "contact"),
    doc_date: f.date("Date", { required: true }),
    due_date: f.date("Due", { level: "more" }),
    number: f.text("Number", { level: "more" }),
    total: f.money("Total", { required: true }),
    memo: f.text("Memo"),
    lines: f.list("Lines", f.object("Line", {
      description: f.text("Description", { required: true }),
      category: f.text("Category"),
      amount: f.money("Amount", { required: true }),
      quantity: f.number("Quantity", { level: "more" }),
      unit: f.text("Unit", { level: "more" }),
      stock_item_id: f.ref("Product", "stock_item", { level: "more" }),
    }), { level: "more", itemLabel: "Line" }),
  },
};

export const ENTITY_LABELS: Record<EntityKind, string> = {
  animal: "Animal", group: "Group", place: "Place", contact: "Contact", stock_item: "Product", stock_lot: "Stock lot", asset: "Equipment", fin_doc: "Financial record",
};
